import type { RPCRouter } from "@granular/fabric3-server";
import type { inferRouterOutputs } from "@trpc/server";
import { useAtom } from "jotai";
import { useCallback, useMemo } from "react";
import { coreQueryClient } from "../services/trpc";
import { fullContext } from "../state/local";
import { useOperatingContext } from "./operatingContext";

type RouterOutput = inferRouterOutputs<RPCRouter>;

export type AgencyResponse = RouterOutput["bizContext"]["getAgencies"];
export type SingleAgencyResponse =
  RouterOutput["bizContext"]["getAgencies"][number];

export function useOperatingAgency() {
  const { contextSelected } = useOperatingContext();
  const [context, setContext] = useAtom(fullContext);

  // Hoegemeyer has an extra Area level (Org -> Area -> Region -> District), so from an
  // Org/Area-rooted context its Districts sit deeper than the roles-svc default depth of 2.
  // Request depth 3 for Hoegemeyer so the agency (District) list isn't cut short; other
  // brands keep the default.
  const agencyMaxDepth =
    contextSelected?.brand === "hoegemeyer" ? 3 : undefined;

  const {
    data,
    isLoading: isInitialLoading,
    isError,
  } = coreQueryClient.bizContext.getAgencies.useQuery(
    {
      contextId: contextSelected?.contextId ?? "",
      maxDepth: agencyMaxDepth,
    },
    {
      enabled:
        !!contextSelected &&
        contextSelected.type !== "District" &&
        contextSelected.type !== "Agency" &&
        contextSelected.type !== "Operation", // only fetch when context is selected

      staleTime: 60 * 1000 * 60 * 24,
      select: (data) => {
        return {
          allAgencies: data,
          allAgenciesByContextId: data.reduce(
            (acc, agency) => {
              acc[agency.contextId] = agency;
              return acc;
            },
            {} as Record<string, SingleAgencyResponse>,
          ),
        };
      },
    },
  );

  const selectedAgency = useMemo(() => {
    return context
      ? data?.allAgenciesByContextId[context.fullString]
      : undefined; //TODO: should we return the first context?
  }, [data, context]);
  /**
   * Set the global business context according to the new context selected.
   * The context selected will be the first valid context found in the following order:
   * 1. The context selected by the user (obviously, if it's valid)
   * 2. The current context (nothing will change)
   * 3. The first context in the allContexts array (fallback)
   * @returns the new context selected or undefined if no valid context was found (if user don't have any context)
   */
  const setValidAgencySelect = useCallback(
    (newAgency: SingleAgencyResponse | undefined) => {
      if (newAgency) {
        setContext(newAgency.contextId);
      }
    },
    [setContext],
  );

  return {
    allAgencies: data?.allAgencies,
    allAgenciesByContextId: data?.allAgenciesByContextId ?? {},
    selectedAgency,
    setSelectedAgency: setValidAgencySelect,
    isLoading: isInitialLoading,
    isError,
  };
}
